/**
 * @fileoverview Centralized API service layer for UI application
 *
 * This module provides a unified API client with consistent error handling,
 * logging, and request/response processing across the entire UI application.
 *
 * Features:
 * - Centralized HTTP client with automatic error handling
 * - Consistent request logging and debugging
 * - Automatic JSON serialization/deserialization
 * - Configurable base URL for different environments
 * - Integration with UI error handling utilities
 *
 * Usage:
 *   import { apiClient } from '../services/api.ts';
 *   const users = await apiClient.get('/users');
 *   const newUser = await apiClient.post('/users', userData);
 */

import { deploymentConfig } from "../deploymentConfig";
import { getAuthEnabled } from "../settings";
import { logger } from "../utils/logger";
import { ApiError, createApiClient } from "@jkast90/kast-common/api-client";
import { isAbort, RequestTimeoutError } from "@jkast90/kast-common/fetch";
import { bypassEmail } from "../auth/bypassEmail";
import { bannerStore } from "../utils/bannerStore";
import { session } from "../auth/session";
import { cache } from "../utils/cacheUtils";
import {
  browserOnline,
  OfflineError,
  OFFLINE_WRITE_MESSAGE,
} from "../utils/connectivity";

/** Only these are retried automatically: repeating them can't change data. */
const RETRYABLE_METHODS = new Set(["GET", "HEAD"]);

/** An Error carrying the HTTP status, so callers can tell a 404 from a 503. */
export interface HttpError extends Error {
  status: number;
}

function httpError(status: number, text: string): HttpError {
  return Object.assign(new Error(`API Error ${status}: ${text}`), { status });
}

export interface RequestOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
  /** The caller's own cancel; combined with the timeout. */
  signal?: AbortSignal;
  /** Per-attempt timeout; defaults to API_TIMEOUT_MS. */
  timeoutMs?: number;
}

/** How long one attempt may take before it is aborted. */
export const API_TIMEOUT_MS = 8000;

/** The error a timed-out request surfaces; retried like a network error. */
export class ApiTimeoutError extends Error {
  readonly timeout = true;
  constructor(ms: number) {
    super(
      `API Error: server unreachable (no response in ${Math.round(ms / 1000)}s)`,
    );
    this.name = "ApiTimeoutError";
  }
}

export interface RequestParams {
  [key: string]: string | number | boolean | undefined | string[];
}

/** Retries of a GET or HEAD, the delay before the first, and how much each wait grows. */
export const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 500;
const RETRY_BACKOFF_MULTIPLIER = 2;

function currentBaseUrl(): string {
  try {
    return deploymentConfig.get("apiUrl");
  } catch {
    // Last resort fallback if the config fails (e.g., in tests)
    // Use current protocol and hostname with appropriate port
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";
    const port = protocol === "https:" ? "8443" : "8000";
    return isLocalhost
      ? `${protocol}//${hostname}:${port}`
      : `${protocol}//${hostname}`;
  }
}

// The mechanics (the bearer, the one refresh and resend after a 401, the time
// limit, GET and HEAD retried after a 5xx, a timeout or a network error, never
// a POST) are kast-common's api-client. The API log is the fetch recorder's
// (main.tsx installs it), so the client records nothing itself. This is the
// wording and the app's own rules: the offline refusal, the 503 banner, and
// X-User-Email for an API running with auth off.
const client = createApiClient({
  baseUrl: currentBaseUrl,
  session,
  retry: {
    retries: MAX_RETRIES,
    delayMs: RETRY_DELAY_MS,
    backoff: RETRY_BACKOFF_MULTIPLIER,
    wait: (ms) => apiClient.sleep(ms),
  },
  recordCalls: false,
  // A network error reaches the caller as the browser raised it.
  onNetworkFailure: (error) => error as Error,
  messages: {
    expired: "Authentication expired. Please log in again.",
    refreshFailed:
      "Could not reach auth server to refresh session. Please try again.",
  },
  failure: async (response, fallback, authorized, retried) => {
    const text = await response.text();
    // Handle 503 Service Unavailable errors with banner
    if (response.status === 503) {
      bannerStore.showError(
        "The service is temporarily unavailable. Please try again in a few moments.",
        5000,
      );
    }
    // A 401 that ended or failed the sign-in has its own message (the session
    // ended, and the sign-in gate takes over; or it is kept and the caller can
    // try later); any other refusal, a resend's included, is the API's text.
    if (authorized && response.status === 401 && !retried)
      return new Error(fallback);
    return httpError(response.status, text);
  },
});

/**
 * Base API client providing HTTP operations with consistent error handling.
 */
class ApiClient {
  /**
   * The wait between retries. Not private so a test can skip the backoff
   * (`vi.spyOn(apiClient, "sleep")`).
   */
  sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Offline (the browser says so, or cached mode after repeated outage
   * failures), a write is refused here, before anything is sent, with a
   * short banner; a read while the browser is offline fails at once instead
   * of waiting on retries, and withCache serves the saved copy. Neither
   * touches the session, so an expired token offline signs nobody out.
   */
  private refuseOffline(method: string): void {
    const write = !RETRYABLE_METHODS.has(method);
    if (write && cache.isReadOnly()) {
      bannerStore.showWarning(OFFLINE_WRITE_MESSAGE, 5000);
      throw new OfflineError();
    }
    if (!browserOnline()) throw new OfflineError("You're offline.");
  }

  async request<T = unknown>(
    endpoint: string,
    options: RequestOptions = {},
  ): Promise<T> {
    const { timeoutMs: timeoutOption, ...init } = options;
    this.refuseOffline((init.method || "GET").toUpperCase());
    const timeoutMs = timeoutOption ?? API_TIMEOUT_MS;
    // X-User-Email names the user only to an API running with auth disabled
    // (it ignores the header otherwise), so it is sent only then.
    const userEmail = getAuthEnabled() ? null : bypassEmail.get();
    try {
      const response = await client.optionalRequest(
        endpoint,
        {
          ...init,
          headers: {
            "Content-Type": "application/json",
            ...(userEmail && { "X-User-Email": userEmail }),
            ...init.headers,
          },
        },
        { timeoutMs },
      );
      // Handle 204 No Content responses
      if (response.status === 204) return null as T;
      const contentType = response.headers.get("content-type");
      return contentType?.includes("application/json")
        ? await response.json()
        : ((await response.text()) as T);
    } catch (error) {
      // The client's own timeout becomes the error this app has always shown.
      if (
        error instanceof ApiError &&
        error.cause instanceof RequestTimeoutError
      )
        throw new ApiTimeoutError(timeoutMs);
      if (!(error instanceof OfflineError) && !isAbort(error)) {
        logger.error(
          `API request failed: ${init.method || "GET"} ${currentBaseUrl()}${endpoint}`,
          error,
        );
      }
      throw error;
    }
  }

  // HTTP method helpers
  async get<T = unknown>(
    endpoint: string,
    params: RequestParams = {},
  ): Promise<T> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        if (Array.isArray(value)) {
          value.forEach((v) => query.append(key, String(v)));
        } else {
          query.append(key, String(value));
        }
      }
    });
    const queryString = query.toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return this.request<T>(url, { method: "GET" });
  }

  async post<T = unknown>(endpoint: string, data: unknown = {}): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async put<T = unknown>(endpoint: string, data: unknown = {}): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async patch<T = unknown>(endpoint: string, data: unknown = {}): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async delete<T = unknown>(endpoint: string, data?: unknown): Promise<T> {
    const options: RequestOptions = { method: "DELETE" };
    if (data) {
      options.body = JSON.stringify(data);
    }
    return this.request<T>(endpoint, options);
  }
}

// Create singleton instance
export const apiClient = new ApiClient();
